"use client";

import React, {useState, useRef, useEffect, ElementType} from "react";
import {Typography, Box, Collapse, SxProps} from "@mui/material";
import {ColletteTextButton} from "@collette-travel/collette-common-controls";
import IntrinsicElements = React.JSX.IntrinsicElements;

export interface ReadMoreToggleProps {
  description: string;
  linesShown?: number;
  testId: string;
  textSx?: SxProps;
  collapseOffScreen?: boolean;
  textComponent?: ElementType<any, keyof IntrinsicElements>;
  buttonColor?: "secondary" | "primary";
}
const ColletteReadMoreToggle = ({
  description,
  linesShown = 6,
  testId,
  textSx,
  collapseOffScreen = false,
  textComponent = "p",
  buttonColor = "secondary"
}:ReadMoreToggleProps) => {
  const [toggled, setToggled] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLParagraphElement | null>(null);

  const [collapsedSize, setCollapsedSize] = useState<number>(0);

  const [clampText, setClampText] = useState<boolean>(true);

  const handleClick = () => {
    if (toggled) {
      // Going from expanded → collapsed
      setToggled(false);
      setTimeout(() => setClampText(true), 250); // Wait for Collapse animation
    } else {
      // Going from collapsed → expanded
      setClampText(false);
      setToggled(true);
    }
  };

  useEffect(() => {
    const checkCollapse = () => {
      if (textRef.current) {
        const computedStyle = window.getComputedStyle(textRef.current);
        const fontSize = parseFloat(computedStyle.fontSize);
        let lineHeight = parseFloat(computedStyle.lineHeight);

        // Handle "normal" lineHeight or NaN cases
        // WHY: lineHeight might be "normal" which parses to NaN
        // WHY: Default to 1.6 * fontSize as a reasonable fallback (matches common line-height values)
        if (Number.isNaN(lineHeight)) {
          lineHeight = fontSize * 1.6;
        }

        const truncatedHeight = lineHeight * linesShown;
        // WHY: Add small tolerance (1px) to account for sub-pixel rendering differences
        setIsTruncated(textRef.current.scrollHeight > truncatedHeight + 1);
        setCollapsedSize(Math.min(textRef.current.scrollHeight, truncatedHeight));
      }
    };

    let resizeObserver: ResizeObserver | null = null;

    // Use ResizeObserver to detect when content is ready
    if (textRef.current) {
      resizeObserver = new ResizeObserver(() => {
        checkCollapse();
      });

      resizeObserver.observe(textRef.current);
    }


    window.addEventListener("resize", checkCollapse); // Check for resizing
    return () => window.removeEventListener("resize", checkCollapse);
  }, [linesShown]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            setToggled(false);
            setTimeout(() => setClampText(true), 250);
          }
        });
      },
      {
        root: null,
        threshold: 0
      }
    );
    
    if (collapseOffScreen) {
      const cleanupRef = textRef.current;
      if (cleanupRef) {
        observer.observe(cleanupRef);
      }
      return () => {
        if (cleanupRef) {
          observer.unobserve(cleanupRef);
        }
      };
    }
    return () => {};
  }, [collapseOffScreen]);

  return (
    <Box>
      <Collapse
        in={toggled}
        collapsedSize={collapsedSize}
        data-testid={`read-more_collapsable_${testId}`}
      >
        <Typography
          ref={textRef}
          variant="body1"
          sx={{
            color: "text.primary",
            fontWeight: "500",
            overflow: "hidden",
            lineClamp: clampText ? linesShown : "none",
            WebkitLineClamp: clampText ? linesShown : "none",
            display: clampText ? "-webkit-box" : "block",
            WebkitBoxOrient: clampText ? "vertical" : undefined,
            textOverflow: clampText ? "ellipsis" : undefined,
            ...textSx
          }}
          data-testid={`read-more_description_${testId}`}
          component={textComponent}
        >
          {description}
        </Typography>
      </Collapse>
      {isTruncated && (
        <ColletteTextButton
          onClick={handleClick}
          testId={`read-more_button_${testId}`}
          label={toggled ? "Read Less" : "Read More"}
          className="read-more-toggle-button"
          color={buttonColor}
          sx={{
            textAlign: "center",
            fontSize: "1.4rem",
            fontStyle: "normal",
            fontWeight: "600",
            lineHeight: "2rem",
            letterSpacing: "0.1px",
            textTransform: "unset",
            ":focus-visible": {
              outline: "solid 1px var(--theme-palette-primary-dark)",
              backgroundColor: "transparent",
              outlineOffset: "-1px"
            }
          }}
          disableFocusRipple
        />
      )}
    </Box>
  );
};

export default ColletteReadMoreToggle;
